import { formatDate } from "date-fns"
import { ChartConfig, ChartContainer } from "design-system/molecules/Chart"
import { cn } from "design-system/utils"
import { Loading } from "modules/shared/components/Loading"
import { MINIATURE_AREA_WIDTH, MINIATURE_BAR_SIZE } from "modules/shared/lib/charts"
import { useMemo } from "react"
import { Area, AreaChart, Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"
import { WeeklyActivityResponseDataSchema } from "schema"
import z from "zod"

type Props = {
  data?: z.infer<typeof WeeklyActivityResponseDataSchema>["weeklyCounts"][number]
  color?: string
  areaChart?: boolean
  className?: string
  isPending: boolean
}

export const ActivityMiniature = ({ data, isPending, color, areaChart, className }: Props) => {
  const noData = useMemo(() => {
    return data?.weeklyCount?.every((item) => item.count === 0)
  }, [data])

  const chartData = useMemo(() => {
    return data?.weeklyCount?.map((item) => {
      return {
        ...item,
        date: formatDate(item.date ?? new Date(), "MMM d"),
      }
    })
  }, [data])

  const maxValue = useMemo(() => {
    return Math.round(Math.max(...(chartData?.map((item) => item.count * 1.1) ?? [])))
  }, [chartData])

  const colorToUse = color ? `var(--${color})` : "var(--chart-primary)"

  const chartConfig = {} satisfies ChartConfig

  const wrapperClassName = cn("min-w-0 overflow-hidden flex justify-end", className)

  if (isPending) {
    return (
      <div className={wrapperClassName}>
        <Loading className="h-8 p-0" />
      </div>
    )
  }

  if (noData) {
    return null
  }

  return (
    <div className={wrapperClassName}>
      <ChartContainer
        config={chartConfig}
        className="aspect-auto h-8"
        style={{
          width: `${(chartData?.length ?? 0) * MINIATURE_AREA_WIDTH}px`,
        }}
      >
        {areaChart ? (
          <AreaChart data={chartData} margin={{ top: 0, bottom: 0, left: 0, right: 0 }}>
            <CartesianGrid vertical={false} horizontal={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={0}
              minTickGap={8}
              hide={true}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={0}
              width={0}
              domain={[0, maxValue]}
              allowDataOverflow
              hide
            />
            <defs>
              <linearGradient id="fillActivity" x1="0" y1="0" x2="0" y2="1">
                <stop offset="10%" stopColor={colorToUse} stopOpacity={0.6} />
                <stop offset="25%" stopColor={colorToUse} stopOpacity={0.3} />
                <stop offset="50%" stopColor={colorToUse} stopOpacity={0.1} />
                <stop offset="75%" stopColor={colorToUse} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area
              type="monotoneX"
              dataKey="count"
              fill="url(#fillActivity)"
              stroke={colorToUse}
              strokeWidth={1}
              dot={false}
              activeDot={false}
            />
          </AreaChart>
        ) : (
          <BarChart data={chartData} margin={{ top: 2, bottom: 2, left: 0, right: 0 }}>
            <CartesianGrid vertical={false} horizontal={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={0}
              minTickGap={8}
              hide={true}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={0}
              width={0}
              domain={[0, maxValue]}
              allowDataOverflow
              hide
            />
            <Bar dataKey="count" stackId="a" fill={colorToUse} barSize={MINIATURE_BAR_SIZE} />
          </BarChart>
        )}
      </ChartContainer>
    </div>
  )
}
